/**
 * Formatting utilities for the NIRC platform.
 * Currency uses INR, dates rendered in IST, sensitive data masked by default.
 * Downstream: data-fetching-and-form-builder agent fills the domain-specific helpers.
 */

/** Format a number as Indian Rupees (INR). */
export function formatINR(amount: number, opts?: { compact?: boolean }): string {
  if (opts?.compact) {
    if (amount >= 1_00_00_000) return `₹${(amount / 1_00_00_000).toFixed(2)}Cr`;
    if (amount >= 1_00_000) return `₹${(amount / 1_00_000).toFixed(2)}L`;
    if (amount >= 1_000) return `₹${(amount / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Parse a value coming from the backend into a correct `Date`.
 *
 * The API serialises timestamps as naive UTC with NO timezone designator
 * (e.g. "2026-06-15T10:41:29.517000" — motor reads Mongo dates back without
 * tzinfo). A bare date-time string like that is parsed by `new Date()` as
 * *local* time, which makes every timestamp render ~5.5h off in IST. We fix it
 * at the boundary: if the string is a date-time with no `Z`/offset, treat it as
 * UTC by appending `Z`. Date-only strings ("2026-06-15") and values that
 * already carry a timezone are left untouched.
 */
export function parseBackendDate(value: Date | string | number): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const s = value.trim();
  // Has a time component (…T HH:MM…) but no trailing Z and no ±HH:MM offset →
  // it's a timezone-less UTC timestamp; mark it as UTC.
  if (/\d{2}:\d{2}/.test(s) && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) {
    return new Date(`${s}Z`);
  }
  return new Date(s);
}

/** Format a date in IST (Asia/Kolkata) with a human-readable format. */
export function formatDateIST(
  date: Date | string | number,
  format: 'date' | 'datetime' | 'time' = 'date'
): string {
  const d = parseBackendDate(date);
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Kolkata',
    ...(format === 'date' && { day: '2-digit', month: 'short', year: 'numeric' }),
    ...(format === 'datetime' && {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    ...(format === 'time' && { hour: '2-digit', minute: '2-digit', hour12: true }),
  };
  return new Intl.DateTimeFormat('en-IN', options).format(d);
}

/** Mask a bank account number — show only last 4 digits. */
export function maskAccountNumber(raw: string): string {
  if (raw.length <= 4) return '****';
  return `${'*'.repeat(raw.length - 4)}${raw.slice(-4)}`;
}

/** Mask an Aadhaar number — show only last 4 digits in XXXX XXXX format. */
export function maskAadhaar(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 12) return '****';
  return `XXXX XXXX ${digits.slice(-4)}`;
}

/** Truncate a long string with an ellipsis. */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 1)}…`;
}
